/**
 * Shared primitives for the /n2 marketing site. Extract every reusable
 * element here so the five pages compose consistently and there's a single
 * place to tune spacing, type sizes, and hover states.
 */

import React, { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { palette } from './theme';

// ---- Section framing -------------------------------------------------------

export const SectionLabel: React.FC<{ left: string; right?: string }> = ({ left, right }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    paddingBottom: 12, borderBottom: `1px solid ${palette.line}`,
    fontFamily: 'var(--sans)', fontSize: 11, letterSpacing: '0.22em',
    textTransform: 'uppercase',
  }}>
    <span style={{ color: palette.ink3, fontWeight: 700 }}>{left}</span>
    {right && (
      <span style={{
        color: palette.ink3, fontWeight: 500, letterSpacing: '0.08em',
        textTransform: 'none', fontSize: 12,
      }}>{right}</span>
    )}
  </div>
);

// ---- Buttons --------------------------------------------------------------

const btnBase: CSSProperties = {
  fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
  letterSpacing: '0.02em', padding: '13px 24px',
  borderRadius: 2, textDecoration: 'none',
  display: 'inline-block', cursor: 'pointer',
  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
};

export const PrimaryButton: React.FC<{
  href?: string; to?: string; children: React.ReactNode;
}> = ({ href, to, children }) => {
  const style: CSSProperties = {
    ...btnBase, background: palette.ink, color: palette.paper,
    border: `1px solid ${palette.ink}`,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = palette.goldDeep;
    (e.currentTarget as HTMLElement).style.borderColor = palette.goldDeep;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = palette.ink;
    (e.currentTarget as HTMLElement).style.borderColor = palette.ink;
  };
  if (to) return <Link to={to} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</Link>;
  return <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</a>;
};

export const SecondaryButton: React.FC<{
  href?: string; to?: string; children: React.ReactNode;
}> = ({ href, to, children }) => {
  const style: CSSProperties = {
    ...btnBase, background: 'transparent', color: palette.ink,
    border: `1px solid ${palette.ink}`,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = palette.ink;
    (e.currentTarget as HTMLElement).style.color = palette.paper;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = 'transparent';
    (e.currentTarget as HTMLElement).style.color = palette.ink;
  };
  if (to) return <Link to={to} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</Link>;
  return <a href={href} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{children}</a>;
};

// ---- Typographic building blocks -----------------------------------------

export const Eyebrow: React.FC<{ children: React.ReactNode; dot?: boolean }> = ({ children, dot = true }) => (
  <div style={{
    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.24em', textTransform: 'uppercase', color: palette.ink3,
    display: 'flex', gap: 12, alignItems: 'center', marginBottom: 28,
  }}>
    {dot && (
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: palette.gold, display: 'inline-block',
      }} />
    )}
    {children}
  </div>
);

export const PageTitle: React.FC<{
  children: React.ReactNode; italic?: React.ReactNode;
}> = ({ children, italic }) => (
  <h1 style={{
    fontFamily: 'var(--serif)', fontWeight: 400,
    fontSize: 60, lineHeight: 1.04, letterSpacing: '-0.015em',
    margin: '0 0 24px', color: palette.ink,
    textWrap: 'balance' as CSSProperties['textWrap'],
  }}>
    {children}
    {italic && <em style={{ fontStyle: 'italic', color: palette.goldDeep }}> {italic}</em>}
  </h1>
);

export const PageLede: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{
    fontFamily: 'var(--serif)', fontStyle: 'italic',
    fontSize: 21, lineHeight: 1.5, color: palette.ink2,
    maxWidth: '54ch', margin: '0 0 40px',
    textWrap: 'balance' as CSSProperties['textWrap'],
  }}>{children}</p>
);

export const SectionHead: React.FC<{
  children: React.ReactNode; italic?: React.ReactNode;
}> = ({ children, italic }) => (
  <h2 style={{
    fontFamily: 'var(--serif)', fontWeight: 400,
    fontSize: 40, lineHeight: 1.1, letterSpacing: '-0.015em',
    margin: '0 0 20px', color: palette.ink,
    textWrap: 'balance' as CSSProperties['textWrap'],
  }}>
    {children}
    {italic && <em style={{ fontStyle: 'italic', color: palette.goldDeep }}> {italic}</em>}
  </h2>
);

export const Body: React.FC<{
  children: React.ReactNode; muted?: boolean; maxWidth?: string;
}> = ({ children, muted, maxWidth = '58ch' }) => (
  <p style={{
    fontFamily: 'var(--sans)', fontSize: 16, lineHeight: 1.7,
    color: muted ? palette.ink3 : palette.ink2,
    maxWidth, margin: '0 0 20px',
  }}>{children}</p>
);

// ---- Card + rule -----------------------------------------------------------

export const GoldAccentCard: React.FC<{
  children: React.ReactNode; padding?: string; onPaper2?: boolean;
}> = ({ children, padding = '40px 40px', onPaper2 = false }) => (
  <div style={{
    background: onPaper2 ? palette.paper2 : palette.paper,
    border: `1px solid ${palette.line}`,
    borderRadius: 4, padding, position: 'relative',
  }}>
    <div style={{
      position: 'absolute', top: 0, left: 40, width: 40, height: 3,
      background: palette.gold,
    }} />
    {children}
  </div>
);

export const Divider: React.FC<{ dashed?: boolean }> = ({ dashed }) => (
  <div style={{
    height: 1, background: dashed ? 'transparent' : palette.line,
    borderTop: dashed ? `1px dashed ${palette.line}` : 'none',
    margin: '24px 0',
  }} />
);

// ---- Preview / sample markers --------------------------------------------

/**
 * Full-width banner at the top of any page whose numbers aren't live yet.
 * Sits above the page eyebrow. Warm caution tone — visible but not alarming.
 * Removed when the corresponding read API + real data is wired.
 */
export const PreviewBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: `${palette.caution}12`,
    borderTop: `1px solid ${palette.caution}40`,
    borderBottom: `1px solid ${palette.caution}40`,
    padding: '14px 32px',
  }}>
    <div style={{
      maxWidth: 1100, margin: '0 auto',
      display: 'flex', gap: 14, alignItems: 'baseline',
      fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.5,
      color: palette.caution,
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.22em',
        textTransform: 'uppercase', whiteSpace: 'nowrap',
        paddingTop: 2,
      }}>Preview</span>
      <span style={{ color: palette.ink2 }}>{children}</span>
    </div>
  </div>
);

/**
 * Inline chip marking a specific number/row as illustrative. Small, quiet,
 * but unmissable when scanning. Use near any fabricated value.
 */
export const SampleChip: React.FC<{ tight?: boolean }> = ({ tight = false }) => (
  <span style={{
    display: 'inline-block',
    fontFamily: 'var(--sans)', fontSize: 9, fontWeight: 700,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    color: palette.caution,
    padding: tight ? '1px 6px' : '2px 8px',
    border: `1px solid ${palette.caution}55`,
    background: `${palette.caution}10`,
    borderRadius: 2, verticalAlign: 'middle',
    marginLeft: tight ? 6 : 8,
  }}>Sample</span>
);

// ---- Page container -------------------------------------------------------

export const PageContainer: React.FC<{
  children: React.ReactNode; narrow?: boolean;
}> = ({ children, narrow = false }) => (
  <div style={{
    maxWidth: narrow ? 760 : 1100, margin: '0 auto',
    padding: '80px 32px 40px',
  }}>{children}</div>
);
